import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from "react";
import type { PulseAction, PulseAnswerType, PulseConfidence, PulseContext as PulseCtxData } from "@/hooks/usePulse";
import { useOrganization } from "@/contexts/OrganizationContext";

export interface PulseMessage {
  id: string;
  role: "user" | "pulse" | "system";
  content: string;
  answerType?: PulseAnswerType;
  confidence?: PulseConfidence;
  actions?: PulseAction[] | null;
  followUpSuggestions?: string[];
  contextSnapshot?: { entityName: string | null; entityType: string | null };
  timestamp: Date;
}

interface PulseContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  messages: PulseMessage[];
  addMessage: (message: PulseMessage) => void;
  clearMessages: () => void;
  currentContext: PulseCtxData | null;
  setCurrentContext: (ctx: PulseCtxData | null) => void;
  pendingPrompt: string | null;
  setPendingPrompt: (p: string | null) => void;
}

const PulseUIContext = createContext<PulseContextValue | undefined>(undefined);

const HISTORY_CAP = 20;
const historyKey = (orgId: string | null | undefined) =>
  `pulse_history_${orgId ?? "default"}`;

function loadHistory(orgId: string | null | undefined): PulseMessage[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = sessionStorage.getItem(historyKey(orgId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Array<Omit<PulseMessage, "timestamp"> & { timestamp: string }>;
    return parsed.map((m) => ({ ...m, timestamp: new Date(m.timestamp) }));
  } catch {
    return [];
  }
}

function saveHistory(orgId: string | null | undefined, messages: PulseMessage[]) {
  if (typeof window === "undefined") return;
  try {
    const trimmed = messages.slice(-HISTORY_CAP);
    sessionStorage.setItem(historyKey(orgId), JSON.stringify(trimmed));
  } catch {
    // quota or serialization issue — silently ignore
  }
}

export function PulseProvider({ children }: { children: ReactNode }) {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id ?? null;

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<PulseMessage[]>(() => loadHistory(orgId));
  const [currentContext, setCurrentContextState] = useState<PulseCtxData | null>(null);
  const [pendingPrompt, setPendingPrompt] = useState<string | null>(null);

  // Re-hydrate when the active org changes (e.g. user switches orgs).
  const lastOrgRef = useRef<string | null>(orgId);
  useEffect(() => {
    if (lastOrgRef.current === orgId) return;
    lastOrgRef.current = orgId;
    setMessages(loadHistory(orgId));
  }, [orgId]);

  // Persist on every change.
  useEffect(() => {
    saveHistory(orgId, messages);
  }, [orgId, messages]);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((v) => !v), []);

  const addMessage = useCallback((message: PulseMessage) => {
    setMessages((prev) => [...prev, message]);
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
    if (typeof window !== "undefined") {
      try { sessionStorage.removeItem(historyKey(orgId)); } catch { /* ignore */ }
    }
  }, [orgId]);

  const setCurrentContext = useCallback(
    (ctx: PulseCtxData | null) => {
      setCurrentContextState((prev) => {
        // If entity changed (and panel is/was used), inject a system separator
        const prevId = prev?.entityId ?? null;
        const nextId = ctx?.entityId ?? null;
        if (prev && prevId !== nextId && messages.length > 0) {
          const label = ctx?.entityName ?? "general mode";
          setMessages((m) => [
            ...m,
            {
              id: `sys-${Date.now()}`,
              role: "system",
              content: `Context switched to ${label}.`,
              answerType: "clarification",
              confidence: "high",
              timestamp: new Date(),
            },
          ]);
        }
        return ctx;
      });
    },
    [messages.length],
  );

  const value = useMemo<PulseContextValue>(
    () => ({
      isOpen,
      open,
      close,
      toggle,
      messages,
      addMessage,
      clearMessages,
      currentContext,
      setCurrentContext,
      pendingPrompt,
      setPendingPrompt,
    }),
    [isOpen, open, close, toggle, messages, addMessage, clearMessages, currentContext, setCurrentContext, pendingPrompt],
  );

  return <PulseUIContext.Provider value={value}>{children}</PulseUIContext.Provider>;
}

export function usePulseUI() {
  const ctx = useContext(PulseUIContext);
  if (!ctx) throw new Error("usePulseUI must be used within PulseProvider");
  return ctx;
}
